import {flattenSegments} from '@/utils/commons';
import {
  ACTIVITY_TYPE,
  isWheelOfFortuneActivityType,
  isVideoActivityType,
  isFreeKickActivityType,
  isBackgammonActivityType
} from '@/types/Activity';

export const buildCommonFields = (fd) => {
  const isWof = isWheelOfFortuneActivityType(fd?.activityType);
  return {
    name: fd.name,
    status: fd.status,
    uuid: fd.uuid,
    id: fd.id,
    ...(fd?.allCustomers === false &&
    Array.isArray(fd?.selectedSegments ?? fd?.segments) &&
    flattenSegments(fd.selectedSegments ?? fd.segments).length > 0
      ? {
          selectedSegments: fd.selectedSegments ?? fd.segments
        }
      : {}),
    allCustomers: fd?.allCustomers,
    segmentExclude: fd?.segmentExclude ?? fd?.excludeSegments,
    ...(Array.isArray(fd?.excludedSegments) && fd.excludedSegments.length > 0
      ? {excludedSegments: fd.excludedSegments}
      : {}),
    ...(fd?.translations &&
      Object.keys(fd.translations).length > 0 && {
        translations: fd.translations
      }),
    ...(!isWof &&
      Array.isArray(fd?.mediaUrls) &&
      fd.mediaUrls.length > 0 && {mediaUrls: fd.mediaUrls})
  };
};

export const getWelcomeActivityOrder = ({formData}) => ({
  activityType: formData.activityType,
  pointsEarned: formData.pointsEarned,
  hasEndDate: formData.hasEndDate,
  startDate: formData.startDate,
  ...(formData.name && {name: formData.name}),
  ...(formData.activityName && {activityName: formData.activityName}),
  ...(formData.uuid && {uuid: formData.uuid}),
  ...(formData.status && {status: formData.status}),
  ...(formData.id && {id: formData.id}),
  ...(formData.hasEndDate && {endDate: formData.endDate})
});

export const getEasyOrderActivityOrder = ({formData}) => ({
  activityType: formData.activityType,
  pointsForSuccessfulUpload: formData.pointsForSuccessfulUpload,
  pointsPerLineItem: formData.pointsPerLineItem,
  photoOrderModalDisplayLimit: formData.photoOrderModalDisplayLimit,
  hasEndDate: formData.hasEndDate,
  startDate: formData.startDate,
  ...(formData.name && {name: formData.name}),
  ...(formData.activityName && {activityName: formData.activityName}),
  ...(formData.uuid && {uuid: formData.uuid}),
  ...(formData.status && {status: formData.status}),
  ...(formData.id && {id: formData.id}),
  ...(formData.hasEndDate && {endDate: formData.endDate})
});

export const getLoginActivityOrder = ({formData}) => ({
  activityType: formData.activityType,
  pointsEarned: formData.pointsEarned,
  ...(formData.dailyLoginLimit && {dailyLoginLimit: formData.dailyLoginLimit}),
  ...(formData.allTimeLoginLimit && {
    allTimeLoginLimit: formData.allTimeLoginLimit
  }),
  startDate: formData.startDate,
  endDate: formData.endDate,
  receivePoints: formData.receivePoints,
  ...(formData.name && {name: formData.name}),
  ...(formData.activityName && {activityName: formData.activityName}),
  ...(formData.uuid && {uuid: formData.uuid}),
  ...(formData.status && {status: formData.status}),
  ...(formData.id && {id: formData.id})
});

export const getPointsOnOrderDeliveryActivityOrder = ({
  formData,
  isCountryCodeFrequency
}) => {
  const fieldsArranged = {
    activityType: formData.activityType,
    activityName: formData.activityName,
    orderType: formData.orderType,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  };
  if (isCountryCodeFrequency) {
    fieldsArranged.numberOfCases = formData.numberOfCases;
    fieldsArranged.pointsEarned = formData.pointsEarned;
  } else {
    fieldsArranged.localCurrency = formData.localCurrency;
    fieldsArranged.pointsEarned = formData.pointsEarned;
  }
  fieldsArranged.startDate = formData.startDate;
  fieldsArranged.endDate = formData.endDate;
  fieldsArranged.hasEndDate = true;
  return fieldsArranged;
};

export const getPointExpirationActivityOrder = ({formData}) => ({
  activityType: formData.activityType,
  noOfDays: formData.noOfDays,
  ...(formData.name && {name: formData.name}),
  ...(formData.activityName && {activityName: formData.activityName}),
  ...(formData.uuid && {uuid: formData.uuid}),
  ...(formData.status && {status: formData.status}),
  ...(formData.id && {id: formData.id})
});

export const getPayByPointsActivityOrder = ({formData}) => ({
  activityType: formData.activityType,
  activityName: formData.activityName,
  pointsToRedeem: formData.pointsToRedeem,
  localCurrencyValue: formData.localCurrencyValue,
  startDate: formData.startDate,
  ...(formData.hasEndDate && {endDate: formData.endDate}),
  hasEndDate: formData.hasEndDate,
  ...(formData.name && {name: formData.name}),
  ...(formData.uuid && {uuid: formData.uuid}),
  ...(formData.status && {status: formData.status}),
  ...(formData.id && {id: formData.id})
});

export const getSurveyActivityOrder = ({formData}) => {
  const ordered = {
    activityType: formData.activityType,
    activityName: formData.activityName,
    pointsEarned: formData.pointsEarned,
    startDate: formData.startDate,
    endDate: formData.endDate,
    allTimeLimit:
      formData.allTimeLimit != null && formData.allTimeLimit !== ''
        ? String(formData.allTimeLimit)
        : formData.allTimeLimit,
    displayOrder:
      formData.displayOrder !== '' &&
      formData.displayOrder !== undefined &&
      formData.displayOrder !== null
        ? formData.displayOrder
        : 0,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  };
  if (
    Array.isArray(formData.customerType) &&
    formData.customerType.length > 0
  ) {
    ordered.customerType = formData.customerType;
  }
  if (formData.url) {
    ordered.url = formData.url;
  }
  return ordered;
};

export const getVideoActivityOrder = ({formData}) => {
  const ordered = {
    activityType: formData.activityType,
    activityName: formData.activityName,
    pointsEarned: formData.pointsEarned,
    startDate: formData.startDate,
    endDate: formData.endDate,
    allTimeLimit:
      formData.allTimeLimit != null && formData.allTimeLimit !== ''
        ? String(formData.allTimeLimit)
        : formData.allTimeLimit,
    displayOrder:
      formData.displayOrder !== '' &&
      formData.displayOrder !== undefined &&
      formData.displayOrder !== null
        ? formData.displayOrder
        : 0,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id})
  };

  return ordered;
};

export const getWheelOfFortuneActivityOrder = ({formData}) => {
  const displayConfig = formData?.displayConfig;
  const budget = displayConfig?.gameConfig?.budget;

  const pointsEarned =
    formData?.pointsEarned ??
    (budget?.amount != null ? String(budget.amount) : undefined);

  const budgetPeriod =
    formData?.budgetPeriod ??
    (budget?.frequency != null
      ? budget.frequency.toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
      : undefined);

  const dailyDisplayStartTime =
    formData?.dailyDisplayStartTime ?? displayConfig?.startTime;
  const dailyDisplayEndTime =
    formData?.dailyDisplayEndTime ?? displayConfig?.endTime;

  const daysOfWeekUnavailable =
    formData?.daysOfWeekUnavailable ?? displayConfig?.daysOfTheWeekUnavailable;
  const dailyPlayLimit = formData?.dailyPlayLimit ?? displayConfig?.dailyLimit;
  return {
    activityType: formData.activityType,
    activityName: formData.activityName,
    pointsEarned,
    budgetPeriod,
    startDate: formData.startDate,
    endDate: formData.endDate,
    dailyDisplayStartTime,
    dailyDisplayEndTime,
    daysOfWeekUnavailable,
    dailyPlayLimit,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
    ...(displayConfig ? {displayConfig} : {}),
    ...(formData?.translations ? {translations: formData.translations} : {}),
    ...(formData?.equalProbability !== undefined
      ? {equalProbability: formData.equalProbability}
      : {}),
    ...(formData?.equalPoints !== undefined
      ? {equalPoints: formData.equalPoints}
      : {}),
    ...(formData?.wofQuizQuestions
      ? {wofQuizQuestions: formData.wofQuizQuestions}
      : {})
  };
};

export const getFreeKickActivityOrder = ({formData}) => {
  // Build displayConfig by merging schedule fields with existing asset
  const displayConfig = {
    startTime: formData.dailyDisplayStartTime,
    endTime: formData.dailyDisplayEndTime,
    daysOfTheWeekUnavailable: formData.daysOfWeekUnavailable,
    dailyLimit: formData.dailyPlayLimit,
    ...(formData?.displayConfig?.asset && {asset: formData.displayConfig.asset})
  };

  return {
    activityType: formData.activityType,
    activityName: formData.activityName,
    pointsEarned: formData.pointsEarned,
    startDate: formData.startDate,
    endDate: formData.endDate,
    dailyDisplayStartTime: formData.dailyDisplayStartTime,
    dailyDisplayEndTime: formData.dailyDisplayEndTime,
    daysOfWeekUnavailable: formData.daysOfWeekUnavailable,
    dailyPlayLimit: formData.dailyPlayLimit,
    ...(formData.name && {name: formData.name}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
    ...(formData.leaderboards && {leaderboards: formData.leaderboards}),
    displayConfig,
    ...(formData.translations && {translations: formData.translations})
  };
};

export const getCurrencyToPointsActivityOrder = ({formData}) => ({
  activityType: formData.activityType,
  activityName: formData.activityName,
  localCurrency: formData.localCurrency,
  pointsEarned: formData.pointsEarned,
  startDate: formData.startDate,
  ...(formData.hasEndDate && {endDate: formData.endDate}),
  hasEndDate: formData.hasEndDate,
  ...(formData.name && {name: formData.name}),
  ...(formData.uuid && {uuid: formData.uuid}),
  ...(formData.status && {status: formData.status}),
  ...(formData.id && {id: formData.id})
});

export const getFormDataWithOrder = ({
  formData,
  activityType,
  isCountryCodeFrequency
}) => {
  const orderBuilders = {
    [ACTIVITY_TYPE.WELCOME_ACTIVITY]: () => getWelcomeActivityOrder({formData}),
    [ACTIVITY_TYPE.LOGIN]: () => getLoginActivityOrder({formData}),
    [ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY]: () =>
      getPointsOnOrderDeliveryActivityOrder({formData, isCountryCodeFrequency}),
    [ACTIVITY_TYPE.POINT_EXPIRATION]: () =>
      getPointExpirationActivityOrder({formData}),
    [ACTIVITY_TYPE.PAY_BY_POINTS]: () =>
      getPayByPointsActivityOrder({formData}),
    [ACTIVITY_TYPE.SURVEY]: () => getSurveyActivityOrder({formData}),
    [ACTIVITY_TYPE.CURRENCY_TO_POINTS]: () =>
      getCurrencyToPointsActivityOrder({formData}),
    [ACTIVITY_TYPE.EASY_ORDER]: () => getEasyOrderActivityOrder({formData})
  };

  const builder = orderBuilders[activityType];
  if (builder) {
    return {...builder(), ...buildCommonFields(formData)};
  }
  if (isWheelOfFortuneActivityType(activityType)) {
    return {
      ...getWheelOfFortuneActivityOrder({formData}),
      ...buildCommonFields(formData)
    };
  }
  if (isVideoActivityType(activityType)) {
    return {
      ...getVideoActivityOrder({formData}),
      ...buildCommonFields(formData)
    };
  }
  if (isFreeKickActivityType(activityType)) {
    return {
      ...getFreeKickActivityOrder({formData}),
      ...buildCommonFields(formData)
    };
  }
  if (isBackgammonActivityType(activityType)) {
    return {
      ...getFreeKickActivityOrder({formData}),
      ...buildCommonFields(formData)
    };
  }
  return undefined;
};
